import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info, Sparkles, TrendingUp } from 'lucide-react';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'points' | 'levelup';
  title: string;
  message?: string;
  points?: number;
  newLevel?: number;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  showPointsToast: (points: number, reason: string) => void;
  showLevelUpToast: (newLevel: number) => void;
  showAchievementToast: (name: string, points: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <AlertCircle className="w-5 h-5 text-red-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
    points: <Sparkles className="w-5 h-5 text-amber-500" />,
    levelup: <TrendingUp className="w-5 h-5 text-purple-500" />,
  };

  const bgColors = {
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    points: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    levelup: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
  };

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-xl border shadow-lg ${bgColors[toast.type]} animate-slide-in-right`}
    >
      <div className="flex-shrink-0">{icons[toast.type]}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 dark:text-white">{toast.title}</p>
          {toast.points !== undefined && toast.type === 'points' && (
            <span className="px-2 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 text-sm font-bold rounded-full">
              +{toast.points}
            </span>
          )}
        </div>
        {toast.message && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{toast.message}</p>
        )}
      </div>
      <button
        onClick={onRemove}
        className="flex-shrink-0 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID();
    const newToast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration
    const duration = toast.duration || (toast.type === 'levelup' ? 5000 : 3000);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showPointsToast = useCallback(
    (points: number, reason: string) => {
      addToast({
        type: 'points',
        title: 'Points Earned!',
        message: reason,
        points,
      });
    },
    [addToast]
  );

  const showLevelUpToast = useCallback(
    (newLevel: number) => {
      const levelTitles: Record<number, string> = {
        1: '🌱 Newcomer',
        2: '🏠 Neighbor',
        3: '⭐ Regular',
        4: '🤝 Helper',
        5: '💪 Contributor',
        6: '🧭 Guide',
        7: '🎓 Senior',
        8: '🏆 Ambassador',
        9: '👑 Elder',
        10: '🔥 Legend',
      };

      addToast({
        type: 'levelup',
        title: '🎉 Level Up!',
        message: `You've reached Level ${newLevel}: ${levelTitles[newLevel] || 'Unknown'}`,
        newLevel,
        duration: 5000,
      });
    },
    [addToast]
  );

  const showAchievementToast = useCallback(
    (name: string, points: number) => {
      addToast({
        type: 'points',
        title: '🏆 Achievement Unlocked!',
        message: `${name} (+${points} points)`,
        points,
        duration: 4000,
      });
    },
    [addToast]
  );

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, showPointsToast, showLevelUpToast, showAchievementToast }}
    >
      {children}

      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onRemove={() => removeToast(toast.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
